import {UnreachableError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {
  trackSearchHintMoreFiltersClick,
  trackSearchNoResultsHelpfulTipClick,
  trackSearchQueryBuilderClick,
} from '../../../components/inboxes/search/searchTrackingHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {PatchTeamPreferencesTeammateError} from '../../../models/api/patchTeamPreferencesModel';
import {findPanelSequence} from '../../../services/panels';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {batchStore} from '../../appBatching';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {fetchExternalChannelTypesAsyncAction} from '../../data/externalChannelTypes/externalChannelTypesActions';
import {navigateCellsAsyncAction} from '../cells/cellsPanelActions';
import {getCellsSearchQueryHash} from '../cells/search/cellsPanelSearchSelectors';
import {PanelActionPayload, setPanelAction} from '../panelsActions';
import {getPanels} from '../panelsSelectors';
import {InboxesPanelId, makeCellsPanelId, makeViewerPanelId, PanelTypesEnum} from '../panelStateBase';
import {navigateViewerAsyncAction} from '../viewer/viewerPanelActions';
import {InboxesParameters} from './parameters/inboxesParameters';
import {emptyInboxesSelection, findOnlySelectedId} from './parameters/inboxesParametersHelpers';
import {getDefaultInboxesParameters} from './parameters/inboxesParametersSelectors';

/*
 * Type enum.
 */

export enum InboxesPanelActionsEnum {
  NAVIGATED = '@@panels/inboxes/NAVIGATED',
  STARTED_EDITING_WORKSPACES = '@@panels/inboxes/STARTED_EDITING_WORKSPACES',
  STOPPED_EDITING_WORKSPACES = '@@panels/inboxes/STOPPED_EDITING_WORKSPACES',
  SHOW_PIN_TAG_ERRORS_BANNER = '@@panels/inboxes/SHOW_PIN_TAG_ERRORS_BANNER',
  DISMISS_PIN_TAG_ERRORS_BANNER = '@@panels/inboxes/DISMISS_PIN_TAG_ERRORS_BANNER',
  TOGGLE_SEARCH_QUERY_BUILDER_DROPDOWN = '@@panels/inboxes/TOGGLE_SEARCH_QUERY_BUILDER_DROPDOWN',
}

/*
 * Types.
 */

type InboxesPanelActionPayload = PanelActionPayload<InboxesPanelId>;

interface InboxesNavigatedActionPayload extends InboxesPanelActionPayload {
  parameters: InboxesParameters;
}

interface InboxesNavigatedAction extends ActionWithPayload<InboxesNavigatedActionPayload> {
  type: InboxesPanelActionsEnum.NAVIGATED;
}

export interface ShowPinTagErrorsBannerActionPayload extends InboxesPanelActionPayload {
  errors: ReadonlyArray<PatchTeamPreferencesTeammateError>;
}
interface ShowPinTagErrorsBannerAction extends ActionWithPayload<ShowPinTagErrorsBannerActionPayload> {
  type: InboxesPanelActionsEnum.SHOW_PIN_TAG_ERRORS_BANNER;
}

interface DismissPinTagErrorsBannerAction extends ActionWithPayload<InboxesPanelActionPayload> {
  type: InboxesPanelActionsEnum.DISMISS_PIN_TAG_ERRORS_BANNER;
}

interface StartedEditingWorkspacesAction extends ActionWithPayload<InboxesPanelActionPayload> {
  type: InboxesPanelActionsEnum.STARTED_EDITING_WORKSPACES;
}

interface StoppedEditingWorkspacesAction extends ActionWithPayload<InboxesPanelActionPayload> {
  type: InboxesPanelActionsEnum.STOPPED_EDITING_WORKSPACES;
}

interface ToggleSearchQueryBuilderDropdownAction extends ActionWithPayload<InboxesPanelActionPayload> {
  type: InboxesPanelActionsEnum.TOGGLE_SEARCH_QUERY_BUILDER_DROPDOWN;
}

/*
 * Action creators.
 */

export function navigateInboxesAsyncAction(
  panelId: InboxesPanelId,
  maybeParameters?: InboxesParameters,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the inboxes panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.INBOXES) {
      throw new Error('Wrong panel type.');
    }

    const existingParameters = panel?.parameters;
    const parameters =
      filterParameters(existingParameters, maybeParameters) ??
      existingParameters ??
      getDefaultInboxesParameters(getState());

    await batchStore(async () => {
      // Navigate the sub-panels.
      const cellsPromise = dispatch(navigateCellsAsyncAction(makeCellsPanelId(panelId), parameters));
      const viewerPromise = dispatch(navigateViewerAsyncAction(makeViewerPanelId(panelId), parameters));

      // When viewing inboxes we may need to have external channel information for dynamic subject titles.
      dispatch(fetchExternalChannelTypesAsyncAction());

      // Update or create the panel.
      if (panel) {
        dispatch<InboxesNavigatedAction>({
          type: InboxesPanelActionsEnum.NAVIGATED,
          payload: {panelId: panel.id, parameters},
        });
      } else {
        dispatch(
          setPanelAction({
            id: panelId,
            type: PanelTypesEnum.INBOXES,
            parameters,
            pinTagBannerErrors: [],
            isEditingWorkspaces: false,
            isSearchQueryBuilderDropdownOpen: false,
            sequence: findPanelSequence(),
          }),
        );
      }

      await Promise.all([cellsPromise, viewerPromise]);
    });
  };
}

function filterParameters(
  existingParameters: InboxesParameters | undefined,
  parameters: InboxesParameters | undefined,
): InboxesParameters | undefined {
  if (!parameters) {
    return undefined;
  }

  // If we navigated directly to a URL ending in /0, clear out the selection so that both P2 and P3 are in a loading
  // state. Otherwise, P3 will show an empty state.
  const selectedId = findOnlySelectedId<number | string>(parameters.selection);
  if (!existingParameters && (selectedId === 0 || selectedId === '0')) {
    return {
      ...parameters,
      selection: emptyInboxesSelection,
    };
  }

  return parameters;
}

export function showPinTagErrorsBanner(
  panelId: InboxesPanelId,
  errors: ReadonlyArray<PatchTeamPreferencesTeammateError>,
): ShowPinTagErrorsBannerAction {
  return {
    type: InboxesPanelActionsEnum.SHOW_PIN_TAG_ERRORS_BANNER,
    payload: {panelId, errors},
  };
}

export function dismissPinTagErrorsBanner(panelId: InboxesPanelId): DismissPinTagErrorsBannerAction {
  return {
    type: InboxesPanelActionsEnum.DISMISS_PIN_TAG_ERRORS_BANNER,
    payload: {panelId},
  };
}

export function startedEditingWorkspacesAction(
  panelId: InboxesPanelId,
  hasRarelyViewedItems: boolean,
): StartedEditingWorkspacesAction {
  // hasRarelyViewedItems tracks whether there are any rarely viewed items when the user views edit mode.
  trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_VIEW, {if_display_auto_suggestions: hasRarelyViewedItems});

  return {
    type: InboxesPanelActionsEnum.STARTED_EDITING_WORKSPACES,
    payload: {panelId},
  };
}

export function stoppedEditingWorkspacesAction(panelId: InboxesPanelId): StoppedEditingWorkspacesAction {
  return {
    type: InboxesPanelActionsEnum.STOPPED_EDITING_WORKSPACES,
    payload: {panelId},
  };
}

export function toggleSearchQueryBuilderDropdownAction(
  panelId: InboxesPanelId,
  source: 'search-bar' | 'hints-bar' | 'no-results-helpful-tip',
): ThunkAction<ToggleSearchQueryBuilderDropdownAction> {
  return (dispatch, getState) => {
    const queryHash = getCellsSearchQueryHash(getState());

    // Track depending on the source of the action.
    switch (source) {
      case 'search-bar':
        trackSearchQueryBuilderClick({queryHash});
        break;

      case 'hints-bar':
        trackSearchHintMoreFiltersClick({queryHash});
        break;

      case 'no-results-helpful-tip':
        trackSearchNoResultsHelpfulTipClick({queryHash});
        break;

      default:
        throw new UnreachableError(source);
    }

    return dispatch({
      type: InboxesPanelActionsEnum.TOGGLE_SEARCH_QUERY_BUILDER_DROPDOWN,
      payload: {panelId},
    });
  };
}

/*
 * Union type.
 */

export type InboxesPanelAction =
  | InboxesNavigatedAction
  | ShowPinTagErrorsBannerAction
  | DismissPinTagErrorsBannerAction
  | StartedEditingWorkspacesAction
  | StoppedEditingWorkspacesAction
  | ToggleSearchQueryBuilderDropdownAction;
